import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Upload, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');

// Downscale on the client so phone photos (5-10MB) travel light
const fileToResizedDataUrl = (file, maxSize = 1280) => new Promise((resolve, reject) => {
  const img = new window.Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL('image/jpeg', 0.88));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('invalid image')); };
  img.src = url;
});

// XMLHttpRequest on purpose: the global fetch interceptor breaks JSON bodies on AI calls
const analyzePhoto = (imageBase64) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('POST', `${API_URL}/api/products/analyze-photo`);
  xhr.setRequestHeader('Content-Type', 'application/json');
  xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);
  xhr.onload = () => {
    try {
      const data = JSON.parse(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(data.detail || 'Error'));
    } catch (e) { reject(new Error('Respuesta inválida')); }
  };
  xhr.onerror = () => reject(new Error('Error de red'));
  xhr.send(JSON.stringify({ image_base64: imageBase64 }));
});

const uploadPhoto = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API_URL}/api/upload/image`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
  const text = await res.text();
  if (!res.ok) throw new Error('upload failed');
  return `${API_URL}${JSON.parse(text).url}`;
};

export const AiPhotoFill = ({ isUSA, onFilled }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setAnalyzing(true);
    setResult(null);
    try {
      const dataUrl = await fileToResizedDataUrl(file);
      const [sheet, imageUrl] = await Promise.all([analyzePhoto(dataUrl), uploadPhoto(file)]);
      onFilled(sheet, imageUrl);
      setResult(sheet);
      toast.success(isUSA ? 'Sheet filled by AI. Review it, add a price and publish.' : 'La IA llenó la ficha. Revísala, pon el precio y publica.');
    } catch (e) {
      toast.error(e.message || (isUSA ? 'Could not analyze the photo' : 'No se pudo analizar la foto'));
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-orange-50 to-white p-5" data-testid="ai-photo-fill-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900">
            {isUSA ? 'Upload a photo and AI fills the sheet' : 'Sube una foto y la IA llena la ficha'}
          </h3>
          <p className="text-sm text-slate-600 mt-0.5">
            {isUSA
              ? 'Name, fabric type, estimated composition, colors and description. You only review, set a price and publish. Free on every plan.'
              : 'Nombre, tipo de tela, composición estimada, colores y descripción. Tú solo revisas, pones precio y publicas. Gratis en todos los planes.'}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button type="button" onClick={() => cameraRef.current?.click()} disabled={analyzing} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="ai-photo-camera-btn">
              {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
              {analyzing ? (isUSA ? 'Analyzing...' : 'Analizando...') : (isUSA ? 'Take photo' : 'Tomar foto')}
            </Button>
            <Button type="button" variant="outline" onClick={() => galleryRef.current?.click()} disabled={analyzing} className="border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="ai-photo-upload-btn">
              <Upload className="w-4 h-4 mr-2" />
              {isUSA ? 'Upload photo' : 'Subir foto'}
            </Button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid="ai-photo-camera-input" />
            <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="ai-photo-upload-input" />
          </div>
          {result && (
            <div className="mt-3 flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="ai-photo-result-note">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                {isUSA ? 'Detected' : 'Detectado'}: <strong>{result.fabric_type || result.name}</strong>
                {result.confidence && ` · ${isUSA ? 'confidence' : 'confianza'} ${result.confidence}`}. {isUSA ? 'Composition is an estimate — adjust if needed.' : 'La composición es estimada; ajústala si hace falta.'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiPhotoFill;
